import type { BrowserOptions } from '@sentry/react';

import { containsSensitiveText } from './sensitive-text.utils';

type StreamedSpan = Parameters<
  NonNullable<BrowserOptions['beforeSendSpan']>
>[0];

const DENIED_PII_HEADERS = ['forwarded', '-ip', 'remote-', 'via', '-user'];

export const SENTRY_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpHeaders: {
    request: { deny: DENIED_PII_HEADERS },
    response: { deny: DENIED_PII_HEADERS },
  },
  httpBodies: [],
  urlQueryParams: { deny: DENIED_PII_HEADERS },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  graphQL: { document: false, variables: false },
  stackFrameVariables: true,
  frameContextLines: 7,
} satisfies NonNullable<BrowserOptions['dataCollection']>;

const SEGMENT_URL_ATTRIBUTES = new Set(['url.full', 'http.url']);

const SEGMENT_NAME_ATTRIBUTE = 'sentry.segment.name';

const REDACTED = '[redacted]';

const SEGMENT_STRIPPED_ATTRIBUTE_PATTERN =
  /^((url|http)\.(query|fragment)$|http\.(request|response)\.(header\.|body\.data$))/;

const USER_ATTRIBUTE_PREFIX = 'user.';

export const scrubUrl = (value: string | undefined): string | undefined => {
  if (!value) return value;

  try {
    const url = new URL(value);
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return value.split('?')[0].split('#')[0];
  }
};

const scrubSegmentAttributes = (attributes: StreamedSpan['attributes']) => {
  for (const key of Object.keys(attributes)) {
    if (SEGMENT_STRIPPED_ATTRIBUTE_PATTERN.test(key)) {
      delete attributes[key];
    } else if (SEGMENT_URL_ATTRIBUTES.has(key)) {
      const value = attributes[key];
      if (typeof value === 'string') {
        attributes[key] = scrubUrl(value);
      }
    }
  }
};

const scrubSegmentName = (segmentName: string): string => {
  const scrubbed = scrubUrl(segmentName) ?? segmentName;
  return containsSensitiveText(scrubbed) ? REDACTED : scrubbed;
};

const scrubSegmentNameAttribute = (attributes: StreamedSpan['attributes']) => {
  const segmentName = attributes[SEGMENT_NAME_ATTRIBUTE];
  if (typeof segmentName !== 'string') return;

  attributes[SEGMENT_NAME_ATTRIBUTE] = scrubSegmentName(segmentName);
};

export const scrubDynamicSamplingContext = (dsc: { transaction?: string }) => {
  if (dsc.transaction) dsc.transaction = scrubSegmentName(dsc.transaction);
};

const removeUserAttributes = (attributes: StreamedSpan['attributes']) => {
  for (const key of Object.keys(attributes)) {
    if (key.startsWith(USER_ATTRIBUTE_PREFIX)) delete attributes[key];
  }
};

const prepareAttributes = (span: StreamedSpan) => {
  removeUserAttributes(span.attributes);
  if (span.is_segment) scrubSegmentAttributes(span.attributes);
  scrubSegmentNameAttribute(span.attributes);
};

export const scrubStreamedSpan = (span: StreamedSpan) => {
  prepareAttributes(span);

  if (
    containsSensitiveText(span.name) ||
    containsSensitiveText(span.attributes)
  ) {
    span.name = REDACTED;
    span.attributes = {};
    return span;
  }

  span.name = scrubUrl(span.name) ?? span.name;

  return span;
};

export const scrubStreamedSpanFields = (span: StreamedSpan) => {
  prepareAttributes(span);

  if (containsSensitiveText(span.attributes)) {
    span.attributes = {};
  }

  span.name = containsSensitiveText(span.name)
    ? REDACTED
    : (scrubUrl(span.name) ?? span.name);

  return span;
};
